import React, { FC } from 'react';
import { Input, Radio, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Form from '~/components/WizardForm';
import buildNamePath from '~/utils/buildNamePath';
import { Alert } from '~/components';
import PepList from './PepList';
import AsteriskText from './AsteriskText';

const n = buildNamePath('companyQuestionnaire');

const textareaAutoSize = { minRows: 2 };

const DeclarationInputs: FC = () => {
  const { t } = useTranslation('ksaApplication');

  return (
    <Container>
      <Typography.Title level={5}>
        {t('Do you hold any position from the below list of categories?')}
      </Typography.Title>
      <Typography.Paragraph>
        <PepList />
      </Typography.Paragraph>
      <Form.Item name={n(['declaration', 'isPep'])} className="DeclarationInputs-Radio">
        <Radio.Group>
          <Radio value>{t('Yes')}</Radio>
          <Radio value={false}>{t('No')}</Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Visible name={n(['declaration', 'isPep'])}>
        <Typography.Text className="DeclarationInputs-Title">
          {t('Please provide details:')}
        </Typography.Text>
        <Form.Item name={n(['declaration', 'pep'])}>
          <Input.TextArea autoSize={textareaAutoSize} placeholder={t('Enter text here')} />
        </Form.Item>
        <Alert
          type="warning"
          message={t(
            'Indicate at least one Manager, Individual Shareholder or Beneficial Owner as a PEP person.'
          )}
          className="DeclarationInputs-PepAlert"
        />
      </Form.Visible>

      <Typography.Title level={5}>
        {t(
          'Is your company directly or indirectly owned/managed by a natural person residing in a sanctioned country*?'
        )}
      </Typography.Title>
      <Form.Item
        name={n(['declaration', 'isManagedInSanctionedCountries'])}
        className="DeclarationInputs-Radio"
      >
        <Radio.Group>
          <Radio value>{t('Yes')}</Radio>
          <Radio value={false}>{t('No')}</Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Visible name={n(['declaration', 'isManagedInSanctionedCountries'])}>
        <Typography.Text className="DeclarationInputs-Title">
          {t('Specify the name of the sanctioned countries:')}
        </Typography.Text>
        <Form.Item name={n(['declaration', 'sanctionedCountriesManagedIn'])}>
          <Input.TextArea autoSize={textareaAutoSize} placeholder={t('Enter text here')} />
        </Form.Item>
      </Form.Visible>

      <Typography.Title level={5}>
        {t('Does your company perform business / transactions on behalf of a third party?')}
      </Typography.Title>
      <Form.Item
        name={n(['declaration', 'isOnBehalfOfThirdParty'])}
        className="DeclarationInputs-Radio"
      >
        <Radio.Group>
          <Radio value>{t('Yes')}</Radio>
          <Radio value={false}>{t('No')}</Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Visible name={n(['declaration', 'isOnBehalfOfThirdParty'])}>
        <Typography.Text className="DeclarationInputs-Title">
          {t('Please provide details:')}
        </Typography.Text>
        <Form.Item name={n(['declaration', 'onBehalfOfThirdParty'])}>
          <Input.TextArea autoSize={textareaAutoSize} placeholder={t('Enter text here')} />
        </Form.Item>
      </Form.Visible>

      <Typography.Title level={5}>
        {t('Is your company registered or incorporated in a sanctioned country?')}
      </Typography.Title>
      <Form.Item
        name={n(['declaration', 'isRegisteredInSanctionedCountry'])}
        className="DeclarationInputs-Radio"
      >
        <Radio.Group>
          <Radio value>{t('Yes')}</Radio>
          <Radio value={false}>{t('No')}</Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Visible name={n(['declaration', 'isRegisteredInSanctionedCountry'])}>
        <Typography.Text className="DeclarationInputs-Title">
          {t('Specify the name of the sanctioned countries:')}
        </Typography.Text>
        <Form.Item name={n(['declaration', 'sanctionedCountryRegisteredIn'])}>
          <Input.TextArea autoSize={textareaAutoSize} placeholder={t('Enter text here')} />
        </Form.Item>
      </Form.Visible>

      <Typography.Title level={5}>
        {t(
          'Does your company have any subsidiary, branch, office or other presence in a sanctioned country?'
        )}
      </Typography.Title>
      <Form.Item
        name={n(['declaration', 'isPresentedInSanctionedCountry'])}
        className="DeclarationInputs-Radio"
      >
        <Radio.Group>
          <Radio value>{t('Yes')}</Radio>
          <Radio value={false}>{t('No')}</Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Visible name={n(['declaration', 'isPresentedInSanctionedCountry'])}>
        <Typography.Text className="DeclarationInputs-Title">
          {t('Specify the sanctioned country and nature of business/operations:')}
        </Typography.Text>
        <Form.Item name={n(['declaration', 'sanctionedCountresPresentedIn'])}>
          <Input.TextArea autoSize={textareaAutoSize} placeholder={t('Enter text here')} />
        </Form.Item>
      </Form.Visible>

      <Typography.Title level={5}>
        {t('Does your company own any assets in a sanctioned country?')}
      </Typography.Title>
      <Form.Item
        name={n(['declaration', 'hasAssetsInSanctionedCountry'])}
        className="DeclarationInputs-Radio"
      >
        <Radio.Group>
          <Radio value>{t('Yes')}</Radio>
          <Radio value={false}>{t('No')}</Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Visible name={n(['declaration', 'hasAssetsInSanctionedCountry'])}>
        <Typography.Text className="DeclarationInputs-Title">
          {t('Please provide details:')}
        </Typography.Text>
        <Form.Item name={n(['declaration', 'sanctionedCountresHavingAssetsIn'])}>
          <Input.TextArea autoSize={textareaAutoSize} placeholder={t('Enter text here')} />
        </Form.Item>
      </Form.Visible>

      <Typography.Title level={5}>
        {t(
          'Does your company trade with the US or in goods that are US origin or in part contain goods of US origin?'
        )}
      </Typography.Title>
      <Form.Item name={n(['declaration', 'isTradingWithUS'])} className="DeclarationInputs-Radio">
        <Radio.Group>
          <Radio value>{t('Yes')}</Radio>
          <Radio value={false}>{t('No')}</Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Visible name={n(['declaration', 'isTradingWithUS'])}>
        <Typography.Text className="DeclarationInputs-Title">
          {t('Please provide details:')}
        </Typography.Text>
        <Form.Item name={n(['declaration', 'tradingWithUSDetails'])}>
          <Input.TextArea autoSize={textareaAutoSize} placeholder={t('Enter text here')} />
        </Form.Item>
      </Form.Visible>

      <Typography.Title level={5}>
        {t(
          'Does your company maintain any relationship with financial institution in a sanctioned country?'
        )}
      </Typography.Title>
      <Form.Item
        name={n(['declaration', 'isFinancialInstitutionInSanctionedCountry'])}
        className="DeclarationInputs-Radio"
      >
        <Radio.Group>
          <Radio value>{t('Yes')}</Radio>
          <Radio value={false}>{t('No')}</Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Visible name={n(['declaration', 'isFinancialInstitutionInSanctionedCountry'])}>
        <Typography.Text className="DeclarationInputs-Title">
          {t('Please provide details:')}
        </Typography.Text>
        <Form.Item name={n(['declaration', 'sanctionedCountresOfFinancialInstitution'])}>
          <Input.TextArea autoSize={textareaAutoSize} placeholder={t('Enter text here')} />
        </Form.Item>
      </Form.Visible>

      <AsteriskText />
    </Container>
  );
};

const Container = styled.div`
  .DeclarationInputs-Radio {
    margin-top: -8px;
  }
  .DeclarationInputs-Title {
    display: block;
    margin-top: -24px;
    margin-bottom: 16px;
  }
  .DeclarationInputs-Asterisk {
    font-size: 12px;
    line-height: 16px;
  }
  .DeclarationInputs-PepAlert {
    margin-bottom: 16px;
  }
`;

export default DeclarationInputs;
